import { gsap } from 'gsap';
import { useEffect, useRef } from 'react';

type Point = {
  x: number;
  y: number;
};

const lerp = (start: number, end: number, factor: number): number => (1 - factor) * start + factor * end;

const getLocalPointerPos = (event: MouseEvent | TouchEvent, rect: DOMRect): Point => {
  let clientX = rect.width / 2;
  let clientY = rect.height / 2;

  if ('touches' in event && event.touches.length > 0) {
    clientX = event.touches[0].clientX;
    clientY = event.touches[0].clientY;
  } else if ('clientX' in event) {
    clientX = event.clientX;
    clientY = event.clientY;
  }

  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
};

const getMouseDistance = (firstPoint: Point, secondPoint: Point): number => {
  const deltaX = firstPoint.x - secondPoint.x;
  const deltaY = firstPoint.y - secondPoint.y;
  return Math.hypot(deltaX, deltaY);
};

class TrailImageItem {
  public element: HTMLDivElement;
  public inner: HTMLDivElement | null;
  public rect: DOMRect;
  private readonly handleResize: () => void;

  constructor(element: HTMLDivElement) {
    this.element = element;
    this.inner = element.querySelector('.content__img-inner');
    this.rect = element.getBoundingClientRect();
    this.handleResize = () => {
      gsap.set(this.element, { opacity: 0, scale: 1, x: 0, y: 0 });
      this.rect = this.element.getBoundingClientRect();
    };

    window.addEventListener('resize', this.handleResize);
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
  }
}

class ImageTrailController {
  private readonly container: HTMLDivElement;
  private readonly images: TrailImageItem[];
  private readonly variant: number;
  private readonly maxOpacity: number;
  private readonly threshold = 70;
  private pointerStarted = false;
  private imageIndex = -1;
  private zIndexValue = 1;
  private activeImagesCount = 0;
  private isIdle = true;
  private animationFrameId: number | null = null;
  private mousePos: Point = { x: 0, y: 0 };
  private lastMousePos: Point = { x: 0, y: 0 };
  private cachedMousePos: Point = { x: 0, y: 0 };
  private readonly handlePointerMove: (event: MouseEvent | TouchEvent) => void;
  private readonly handlePointerLeave: () => void;
  private readonly renderFrame: () => void;

  constructor(container: HTMLDivElement, variant: number, maxOpacity: number) {
    this.container = container;
    this.variant = variant;
    this.maxOpacity = maxOpacity;
    this.images = Array.from(container.querySelectorAll<HTMLDivElement>('.content__img')).map((image) => new TrailImageItem(image));

    this.handlePointerMove = (event) => {
      const rect = this.container.getBoundingClientRect();
      this.mousePos = getLocalPointerPos(event, rect);

      if (!this.pointerStarted) {
        this.pointerStarted = true;
        this.lastMousePos = { ...this.mousePos };
        this.cachedMousePos = { ...this.mousePos };
        this.renderFrame();
      }
    };

    this.handlePointerLeave = () => {
      this.lastMousePos = { ...this.mousePos };
    };

    this.renderFrame = () => {
      const distance = getMouseDistance(this.mousePos, this.lastMousePos);
      this.cachedMousePos.x = lerp(this.cachedMousePos.x, this.mousePos.x, 0.12);
      this.cachedMousePos.y = lerp(this.cachedMousePos.y, this.mousePos.y, 0.12);

      if (distance > this.threshold) {
        this.showNextImage();
        this.lastMousePos = { ...this.mousePos };
      }

      if (this.isIdle && this.zIndexValue !== 1) {
        this.zIndexValue = 1;
      }

      this.animationFrameId = window.requestAnimationFrame(this.renderFrame);
    };

    this.container.addEventListener('mousemove', this.handlePointerMove);
    this.container.addEventListener('touchmove', this.handlePointerMove, { passive: true });
    this.container.addEventListener('mouseleave', this.handlePointerLeave);
  }

  private showNextImage() {
    if (this.images.length === 0) {
      return;
    }

    this.zIndexValue += 1;
    this.imageIndex = this.imageIndex < this.images.length - 1 ? this.imageIndex + 1 : 0;
    const image = this.images[this.imageIndex];

    gsap.killTweensOf(image.element);
    if (image.inner) {
      gsap.killTweensOf(image.inner);
    }

    const startX = this.cachedMousePos.x - image.rect.width / 2;
    const startY = this.cachedMousePos.y - image.rect.height / 2;
    const endX = this.mousePos.x - image.rect.width / 2;
    const endY = this.mousePos.y - image.rect.height / 2;

    const timeline = gsap.timeline({
      onStart: () => this.onImageActivated(),
      onComplete: () => this.onImageDeactivated(),
    });

    timeline.fromTo(
      image.element,
      {
        opacity: this.maxOpacity,
        scale: this.variant === 2 ? 0.35 : 1,
        zIndex: this.zIndexValue,
        x: startX,
        y: startY,
      },
      {
        duration: 0.42,
        ease: 'power2.out',
        scale: 1,
        x: endX,
        y: endY,
      },
      0,
    );

    if (this.variant === 2 && image.inner) {
      timeline.fromTo(
        image.inner,
        {
          scale: 1.25,
          filter: 'brightness(160%) saturate(115%)',
        },
        {
          duration: 0.42,
          ease: 'power2.out',
          scale: 1,
          filter: 'brightness(100%) saturate(100%)',
        },
        0,
      );
    }

    timeline.to(
      image.element,
      {
        duration: 0.46,
        ease: 'power3.in',
        opacity: 0,
        scale: 0.22,
      },
      0.38,
    );
  }

  private onImageActivated() {
    this.activeImagesCount += 1;
    this.isIdle = false;
  }

  private onImageDeactivated() {
    this.activeImagesCount -= 1;
    if (this.activeImagesCount <= 0) {
      this.activeImagesCount = 0;
      this.isIdle = true;
    }
  }

  destroy() {
    this.container.removeEventListener('mousemove', this.handlePointerMove);
    this.container.removeEventListener('touchmove', this.handlePointerMove);
    this.container.removeEventListener('mouseleave', this.handlePointerLeave);

    if (this.animationFrameId !== null) {
      window.cancelAnimationFrame(this.animationFrameId);
    }

    this.images.forEach((image) => {
      gsap.killTweensOf(image.element);
      if (image.inner) {
        gsap.killTweensOf(image.inner);
      }
      image.destroy();
    });
  }
}

type ImageTrailProps = {
  items?: string[];
  variant?: number;
  className?: string;
  imageClassName?: string;
  maxOpacity?: number;
};

export default function ImageTrail({
  items = [],
  variant = 1,
  className = '',
  imageClassName = '',
  maxOpacity = 1,
}: ImageTrailProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current || items.length === 0) {
      return undefined;
    }

    const controller = new ImageTrailController(containerRef.current, variant, maxOpacity);
    return () => controller.destroy();
  }, [items, maxOpacity, variant]);

  return (
    <div className={`relative h-full w-full overflow-hidden ${className}`.trim()} ref={containerRef}>
      {items.map((url, index) => (
        <div
          className={`content__img pointer-events-none absolute left-0 top-0 aspect-[0.9] w-[160px] overflow-hidden rounded-[24px] border border-white/35 bg-white/10 opacity-0 shadow-[0_30px_70px_-40px_rgba(15,23,42,0.9)] backdrop-blur-sm will-change-transform ${imageClassName}`.trim()}
          key={`${url}-${index}`}
        >
          <div
            className="content__img-inner absolute -left-3 -top-3 h-[calc(100%+24px)] w-[calc(100%+24px)] bg-cover bg-center"
            style={{ backgroundImage: `url(${url})` }}
          />
        </div>
      ))}
    </div>
  );
}
